// Card shown between rounds. Surfaces accuracy / WPM / most-missed key
// and a short encouraging sentence, then a Continue button.

import { useEnterAction } from '../lib/useEnterAction';
import { roundFeedback, topWeakKeys } from '../lib/stats';
import type { RoundResult } from '../types';
import type { PreparedRound } from '../lib/lessonGenerator';

interface RoundIntermissionProps {
  round: PreparedRound;
  result: RoundResult;
  isLast: boolean;
  onContinue: () => void;
}

export default function RoundIntermission({
  round,
  result,
  isLast,
  onContinue,
}: RoundIntermissionProps) {
  const missed = topWeakKeys(result.errorsByKey, 1)[0];
  useEnterAction(onContinue);

  return (
    <div className="surface mx-auto w-full max-w-xl p-6 text-center animate-fade-in-up">
      <p className="label">Runde {round.index + 1} af {round.total}</p>
      <h2 className="mt-2 text-2xl font-semibold text-strong">
        {round.template.title}
      </h2>

      <div className="mt-5 grid grid-cols-2 gap-3 text-left sm:grid-cols-4">
        <MiniStat label="Nøjagtighed" value={`${(result.accuracy * 100).toFixed(0)}%`} />
        <MiniStat label="WPM" value={result.wpm.toFixed(0)} />
        <MiniStat label="Tid" value={formatDuration(result.durationMs)} />
        <MiniStat
          label="Mest tøvet på"
          value={
            missed
              ? missed.key === ' '
                ? '␣'
                : missed.key.toUpperCase()
              : '—'
          }
        />
      </div>

      {!result.completed && (
        <p className="mt-4 text-sm text-amber-700 dark:text-amber-300">
          Du afsluttede runden tidligt ({result.correctChars} af{' '}
          {result.targetLength} tegn).
        </p>
      )}

      <p className="mt-4 text-sm text-muted">{roundFeedback(result)}</p>

      <button
        type="button"
        autoFocus
        onClick={onContinue}
        className="mt-6 rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30"
      >
        {isLast ? 'Se opsummering' : 'Næste runde'}
      </button>
      <p className="mt-2 text-xs text-faint">Tryk Enter for at fortsætte</p>
    </div>
  );
}

function formatDuration(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface-soft px-3 py-2">
      <p className="label text-[10px]">{label}</p>
      <p className="mt-0.5 font-mono text-lg text-strong">{value}</p>
    </div>
  );
}
